/**
 * Admin Webhooks Management Page
 * Complete webhook management interface
 */

import React, { useState } from 'react';
import { WebhookList } from '../../components/webhooks/WebhookList';
import { WebhookForm } from '../../components/webhooks/WebhookForm';
import { WebhookTestModal } from '../../components/webhooks/WebhookTestModal';
import { WebhookLogs } from '../../components/webhooks/WebhookLogs';

interface Webhook {
  id: number;
  name: string;
  webhookUrl: string;
  webhookType: string;
  eventType: string;
  isEnabled: boolean;
  mentionRole: boolean;
  roleId?: string;
  customMessage?: string;
  colorOverride?: string;
  createdAt: string;
  updatedAt: string;
}

type ViewMode = 'list' | 'create' | 'edit' | 'logs';

export const WebhooksPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null);
  const [testingWebhook, setTestingWebhook] = useState<Webhook | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleCreateNew = () => {
    setSelectedWebhook(null);
    setViewMode('create');
  };

  const handleEdit = (webhook: Webhook) => {
    setSelectedWebhook(webhook);
    setViewMode('edit');
  };

  const handleTest = (webhook: Webhook) => {
    setTestingWebhook(webhook);
  };

  const handleFormSuccess = () => {
    setViewMode('list');
    setSelectedWebhook(null);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleFormCancel = () => {
    setViewMode('list');
    setSelectedWebhook(null);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white">Webhook Management</h1>
              <p className="text-slate-400 mt-2">
                Configure Discord webhooks for system events
              </p>
            </div>

            {viewMode === 'list' && (
              <button
                onClick={handleCreateNew}
                className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition-colors flex items-center space-x-2"
              >
                <span>+</span>
                <span>Create Webhook</span>
              </button>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-2 mb-6 border border-slate-700/50">
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('list')}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'list' || viewMode === 'create' || viewMode === 'edit'
                  ? 'bg-orange-500 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              Webhooks
            </button>
            <button
              onClick={() => setViewMode('logs')}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                viewMode === 'logs'
                  ? 'bg-orange-500 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              Delivery Logs
            </button>
          </div>
        </div>

        {/* Content */}
        <div>
          {viewMode === 'list' && (
            <div>
              <WebhookList
                key={refreshTrigger}
                onEdit={handleEdit}
                onTest={handleTest}
              />
            </div>
          )}

          {viewMode === 'create' && (
            <div>
              <div className="mb-6">
                <button
                  onClick={() => setViewMode('list')}
                  className="text-orange-400 hover:text-orange-300 flex items-center space-x-2"
                >
                  <span>←</span>
                  <span>Back to Webhooks</span>
                </button>
              </div>

              <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
                <h2 className="text-2xl font-bold text-white mb-6">
                  Create New Webhook
                </h2>
                <WebhookForm
                  onSuccess={handleFormSuccess}
                  onCancel={handleFormCancel}
                />
              </div>
            </div>
          )}

          {viewMode === 'edit' && selectedWebhook && (
            <div>
              <div className="mb-6">
                <button
                  onClick={() => setViewMode('list')}
                  className="text-orange-400 hover:text-orange-300 flex items-center space-x-2"
                >
                  <span>←</span>
                  <span>Back to Webhooks</span>
                </button>
              </div>

              <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50">
                <h2 className="text-2xl font-bold text-white mb-6">
                  Edit Webhook: {selectedWebhook.name}
                </h2>
                <WebhookForm
                  webhook={selectedWebhook}
                  onSuccess={handleFormSuccess}
                  onCancel={handleFormCancel}
                />
              </div>
            </div>
          )}

          {viewMode === 'logs' && (
            <div>
              <WebhookLogs />
            </div>
          )}
        </div>

        {/* Help Section */}
        {viewMode === 'list' && (
          <div className="mt-8 bg-orange-900/20 border border-orange-500 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-orange-400 mb-3">
              📚 Getting Started with Webhooks
            </h3>
            <div className="text-slate-300 space-y-2">
              <p>
                <strong>1. Create a Discord Webhook:</strong> Go to your Discord
                server → Server Settings → Integrations → Webhooks → New Webhook
              </p>
              <p>
                <strong>2. Copy the Webhook URL:</strong> Click "Copy Webhook URL"
                button
              </p>
              <p>
                <strong>3. Create Webhook Here:</strong> Click "Create Webhook"
                above, paste the URL, select an event type
              </p>
              <p>
                <strong>4. Test Your Webhook:</strong> Use the "Test" button to send
                a sample message
              </p>
              <p className="mt-4 text-sm text-slate-400">
                💡 <strong>Tip:</strong> You can create multiple webhooks for
                different event types or different Discord channels
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Test Modal */}
      {testingWebhook && (
        <WebhookTestModal
          webhook={testingWebhook}
          onClose={() => setTestingWebhook(null)}
        />
      )}
    </div>
  );
};

export default WebhooksPage;
